import React, { useEffect, useRef, useState } from 'react';
import { CornerDownLeft } from 'lucide-react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

interface OnboardingStepInputProps {
  question: string;
  subtitle: string;
  placeholder?: string;
  onAnswer: (value: string) => void;
}

const OnboardingStepInput: React.FC<OnboardingStepInputProps> = ({ question, subtitle, placeholder, onAnswer }) => {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && value.trim()) {
      onAnswer(value.trim());
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}>
      <p className="text-white/40 text-sm mb-2">{subtitle}</p>
      <h2 className="text-2xl md:text-3xl font-semibold text-white mb-8">{question}</h2>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        maxLength={40}
        placeholder={placeholder}
        className="w-full bg-transparent border border-white/20 rounded-lg px-4 py-3 text-white text-lg placeholder-white/30 outline-none focus:border-[#7c6cf6] focus:ring-1 focus:ring-[#7c6cf6] transition-colors"
      />
      <p className="text-white/20 text-xs mt-4 flex items-center gap-1.5">
        {t('Agent.OnboardingPressEnter')} <CornerDownLeft className="w-3 h-3" />
      </p>
    </motion.div>
  );
};

export default OnboardingStepInput;
